import update from "immutability-helper";
import { getRandomColor } from "libs/colors";
import { V3 } from "libs/mjs";
import isEqual from "lodash-es/isEqual";
import reduce from "lodash-es/reduce";
import uniqWith from "lodash-es/uniqWith";
import type { AdditionalCoordinate } from "types/api_types";
import defaultState from "viewer/default_state";
import { maybeGetSomeTracing } from "viewer/model/accessors/tracing_accessor";
import { getExtentForNewBoundingBox } from "viewer/model/accessors/view_mode_accessor";
import type { Action } from "viewer/model/actions/actions";
import { updateKey, updateKey2 } from "viewer/model/helpers/deep_update";
import type { MeshInformation, UserBoundingBox, WebknossosState } from "viewer/store";
import { getTransformedDatasetBoundingBox } from "../accessors/dataset_layer_transformation_accessor";
import { getAdditionalCoordinatesAsString } from "../accessors/flycam_accessor";
import { getMeshesForAdditionalCoordinates } from "../accessors/volumetracing_accessor";
import type { ChangeUserBoundingBoxAction } from "../actions/annotation_actions";
import BoundingBox from "../bucket_data_handling/bounding_box";

const updateAnnotation = (
  state: WebknossosState,
  shape: Partial<WebknossosState["annotation"]>,
): WebknossosState => updateKey(state, "annotation", shape);

const updateUserBoundingBoxes = (
  state: WebknossosState,
  userBoundingBoxes: Array<UserBoundingBox>,
) => {
  const updaterObject = {
    userBoundingBoxes: {
      $set: userBoundingBoxes,
    },
  };
  // We mirror/sync the user bounding boxes between all tracing objects.
  const newVolumes = state.annotation.volumes.map((volumeTracing) => ({
    ...volumeTracing,
    userBoundingBoxes,
  }));
  const maybeSkeletonUpdater = state.annotation.skeleton
    ? {
        skeleton: updaterObject,
      }
    : {};
  const maybeVolumeUpdater = {
    volumes: {
      $set: newVolumes,
    },
  };
  const maybeReadOnlyUpdater = state.annotation.readOnly
    ? {
        readOnly: updaterObject,
      }
    : {};
  return update(state, {
    annotation: {
      ...maybeSkeletonUpdater,
      ...maybeVolumeUpdater,
      ...maybeReadOnlyUpdater,
    },
  });
};

export const updateUserBoundingBox = (
  state: WebknossosState,
  action: ChangeUserBoundingBoxAction,
) => {
  const tracing = maybeGetSomeTracing(state.annotation);

  if (tracing == null) {
    return state;
  }

  const updatedUserBoundingBoxes = tracing.userBoundingBoxes.map((bbox) => {
    if (bbox.id === action.id) {
      const newBox = {
        ...bbox,
        ...action.newProps,
      };
      if (action.newProps.boundingBox != null) {
        // If the boundingBox min/max properties are changed, ensure
        // that these are integer (otherwise, the backend will reject
        // the update actions).
        const { boundingBox } = action.newProps;
        newBox.boundingBox = {
          ...boundingBox,
          min: boundingBox.min.some((el) => Math.floor(el) !== el)
            ? V3.floor(boundingBox.min)
            : boundingBox.min,
          max: boundingBox.max.some((el) => Math.floor(el) !== el)
            ? V3.floor(boundingBox.max)
            : boundingBox.max,
        };
      }
      return newBox;
    }

    return bbox;
  });
  return updateUserBoundingBoxes(state, updatedUserBoundingBoxes);
};

const maybeAddAdditionalCoordinatesToMeshState = (
  state: WebknossosState,
  additionalCoordinates: AdditionalCoordinate[] | null | undefined,
  layerName: string,
) => {
  if (getMeshesForAdditionalCoordinates(state, additionalCoordinates, layerName) == null) {
    const additionalCoordKey = getAdditionalCoordinatesAsString(additionalCoordinates);
    return update(state, {
      localSegmentationStateByLayer: {
        [layerName]: {
          meshes: {
            [additionalCoordKey]: { $set: {} },
          },
        },
      },
    });
  }
  return state;
};

function AnnotationReducer(state: WebknossosState, action: Action): WebknossosState {
  switch (action.type) {
    case "INITIALIZE_ANNOTATION": {
      const resetStoreState = update(state, {
        save: {
          // rebaseRelevantServerAnnotationState stores rebasing relevant information of the annotation.
          // It always is in sync with the latest known version on the server. After initializing it is the current version.
          // mappingDataByLayer entries are initialized automatically when the mappings are loaded by the saga.
          rebaseRelevantServerAnnotationState: {
            annotationVersion: {
              $set: action.annotation.version,
            },
            annotationDescription: {
              $set: action.annotation.description,
            },
            mappingDataByLayer: {
              $set: {},
            },
          },
        },
        // Also reset localAnnotationState. Note that localSegmentationStateByLayer is reset in the reducer
        // that handles SET_DATASET.
        localAnnotationState: {
          $set: defaultState.localAnnotationState,
        },
      });

      return updateAnnotation(resetStoreState, {
        // Clear all tracings. These will be initialized in corresponding
        // initialization actions.
        mappings: [],
        skeleton: undefined,
        volumes: [],
        ...action.annotation,
      });
    }

    case "SET_ANNOTATION_NAME": {
      const { name } = action;
      return updateAnnotation(state, {
        name,
      });
    }

    case "SET_ANNOTATION_VISIBILITY": {
      const { visibility } = action;
      return updateAnnotation(state, {
        visibility,
      });
    }

    case "EDIT_ANNOTATION_LAYER": {
      const newAnnotationLayers = state.annotation.annotationLayers.map((layer) => {
        if (layer.tracingId !== action.tracingId) {
          return layer;
        } else {
          return { ...layer, ...action.layerProperties };
        }
      });
      return updateAnnotation(state, {
        annotationLayers: newAnnotationLayers,
      });
    }

    case "SET_ANNOTATION_DESCRIPTION": {
      const { description } = action;
      return updateAnnotation(state, {
        description,
      });
    }

    case "SET_ANNOTATION_ALLOW_UPDATE": {
      const { currentlyAllowUpdate } = action;
      return updateKey(state, "annotation", {
        isUpdatingCurrentlyAllowed: currentlyAllowUpdate,
      });
    }

    case "SET_USER_BOUNDING_BOXES": {
      return updateUserBoundingBoxes(state, action.userBoundingBoxes);
    }

    case "CHANGE_USER_BOUNDING_BOX": {
      const updatedState = updateUserBoundingBox(state, action);
      return updateKey(updatedState, "uiInformation", {
        activeUserBoundingBoxId: action.id,
      });
    }

    case "ADD_NEW_USER_BOUNDING_BOX": {
      const tracing = maybeGetSomeTracing(state.annotation);

      if (tracing == null) {
        return state;
      }

      const { userBoundingBoxes } = tracing;
      const boundingBoxId = action.id;

      const { min, max, halfBoxExtent } = getExtentForNewBoundingBox(state);
      const newBoundingBoxTemplate: UserBoundingBox = {
        boundingBox: {
          min,
          max,
        },
        id: boundingBoxId,
        name: `Bounding box ${boundingBoxId}`,
        color: getRandomColor(),
        isVisible: true,
      };

      if (action.center != null) {
        newBoundingBoxTemplate.boundingBox = {
          min: V3.toArray(V3.round(V3.sub(action.center, halfBoxExtent))),
          max: V3.toArray(V3.round(V3.add(action.center, halfBoxExtent))),
        };
      }
      let newUserBoundingBox: UserBoundingBox;
      if (action.newBoundingBox != null) {
        newUserBoundingBox = {
          ...newBoundingBoxTemplate,
          ...action.newBoundingBox,
        };
      } else {
        newUserBoundingBox = newBoundingBoxTemplate;
      }

      // Ensure the new bounding box is within the dataset bounding box.
      const datasetBoundingBox = getTransformedDatasetBoundingBox(
        state.dataset,
        state.datasetConfiguration.nativelyRenderedLayerName,
      );
      const newBoundingBox = new BoundingBox(newUserBoundingBox.boundingBox);
      const newBoundingBoxWithinDataset = newBoundingBox.intersectedWith(datasetBoundingBox);
      // Only update the bounding box if the bounding box overlaps with the dataset bounds.
      // Else the bounding box is completely outside the dataset bounds -> in that case just keep the bounding box and let the user cook.
      if (newBoundingBoxWithinDataset.getVolume() > 0) {
        newUserBoundingBox.boundingBox = newBoundingBoxWithinDataset.toBoundingBoxMinMaxType();
      }

      const updatedUserBoundingBoxes = [...userBoundingBoxes, newUserBoundingBox];
      const updatedState = updateUserBoundingBoxes(state, updatedUserBoundingBoxes);
      return updateKey(updatedState, "uiInformation", {
        activeUserBoundingBoxId: newUserBoundingBox.id,
      });
    }

    case "ADD_USER_BOUNDING_BOXES": {
      const { userBoundingBoxes, boundingBoxIds } = action;
      const tracing = maybeGetSomeTracing(state.annotation);
      const hasEqualAmountOfIds = userBoundingBoxes.length === boundingBoxIds.length;
      if (tracing == null || !hasEqualAmountOfIds) {
        return state;
      }

      const additionalUserBoundingBoxes = userBoundingBoxes.map((bb, index) => ({
        ...bb,
        id: boundingBoxIds[index],
      }));
      const mergedUserBoundingBoxes = uniqWith(
        [...tracing.userBoundingBoxes, ...additionalUserBoundingBoxes],
        (bboxWithId1, bboxWithId2) => {
          const { id: _id1, ...bbox1 } = bboxWithId1;
          const { id: _id2, ...bbox2 } = bboxWithId2;
          return isEqual(bbox1, bbox2);
        },
      );
      return updateUserBoundingBoxes(state, mergedUserBoundingBoxes);
    }

    case "DELETE_USER_BOUNDING_BOX": {
      const tracing = maybeGetSomeTracing(state.annotation);

      if (tracing == null) {
        return state;
      }

      const updatedUserBoundingBoxes = tracing.userBoundingBoxes.filter(
        (bbox) => bbox.id !== action.id,
      );
      const updatedState = updateUserBoundingBoxes(state, updatedUserBoundingBoxes);
      if (action.id === state.uiInformation.activeUserBoundingBoxId) {
        return updateKey(updatedState, "uiInformation", {
          activeUserBoundingBoxId: null,
        });
      }
      return updatedState;
    }

    case "UPDATE_MESH_VISIBILITY": {
      const { layerName, id, visibility, additionalCoordinates } = action;
      const additionalCoordKey = getAdditionalCoordinatesAsString(additionalCoordinates);
      return update(state, {
        localSegmentationStateByLayer: {
          [layerName]: {
            meshes: {
              [additionalCoordKey]: {
                [id.toString()]: {
                  isVisible: {
                    $set: visibility,
                  },
                },
              },
            },
          },
        },
      });
    }

    case "UPDATE_MESH_OPACITY": {
      const { layerName, id, opacity } = action;
      const meshDict = state.localSegmentationStateByLayer[layerName].meshes;
      if (meshDict == null) return state;
      const currentAdditionalCoordinates = Object.keys(meshDict || {});
      const updatedMeshes = reduce(
        currentAdditionalCoordinates,
        (updatedMeshesDict, additionalCoordKey) => {
          const meshes = updatedMeshesDict[additionalCoordKey];
          if (meshes == null) return updatedMeshesDict;
          return {
            ...updatedMeshesDict,
            [additionalCoordKey]: update(meshes, {
              [id.toString()]: {
                opacity: {
                  $set: opacity,
                },
              },
            }),
          };
        },
        meshDict,
      );
      return update(state, {
        localSegmentationStateByLayer: {
          [layerName]: {
            meshes: {
              $set: updatedMeshes,
            },
          },
        },
      });
    }

    case "REMOVE_MESH": {
      const { layerName, segmentId } = action;
      const newMeshes: Record<string, Record<string, MeshInformation>> = {};
      const additionalCoordinates = state.flycam.additionalCoordinates;
      const additionalCoordKey = getAdditionalCoordinatesAsString(additionalCoordinates);
      const maybeMeshes = getMeshesForAdditionalCoordinates(
        state,
        additionalCoordinates,
        layerName,
      );
      if (maybeMeshes == null || maybeMeshes[segmentId.toString()] == null) {
        // No meshes exist for the segment id. No need to do anything.
        console.log("Could not find mesh", segmentId, "which was requested to be removed");
        return state;
      }
      const { [segmentId.toString()]: _, ...remainingMeshes } = maybeMeshes as Record<
        string,
        MeshInformation
      >;
      newMeshes[additionalCoordKey] = remainingMeshes;
      return update(state, {
        localSegmentationStateByLayer: {
          [layerName]: {
            meshes: {
              $merge: newMeshes,
            },
          },
        },
      });
    }

    // Mesh information is stored in three places: the state in the store, segment_view_controller and within the mesh sagas.
    case "ADD_AD_HOC_MESH": {
      const {
        layerName,
        segmentId,
        seedPosition,
        seedAdditionalCoordinates,
        mappingName,
        mappingType,
        opacity,
        isVisible,
      } = action;
      const meshInfo: MeshInformation = {
        segmentId: segmentId,
        seedPosition,
        seedAdditionalCoordinates,
        isLoading: false,
        isVisible,
        isPrecomputed: false,
        opacity,
        mappingName,
        mappingType,
      };
      const additionalCoordinates = state.flycam.additionalCoordinates;
      const additionalCoordKey = getAdditionalCoordinatesAsString(additionalCoordinates);

      const stateWithCurrentAddCoords = maybeAddAdditionalCoordinatesToMeshState(
        state,
        additionalCoordinates,
        layerName,
      );

      const updatedKey = update(stateWithCurrentAddCoords, {
        localSegmentationStateByLayer: {
          [layerName]: {
            meshes: {
              [additionalCoordKey]: {
                [segmentId.toString()]: {
                  $set: meshInfo,
                },
              },
            },
          },
        },
      });
      return updatedKey;
    }

    case "ADD_PRECOMPUTED_MESH": {
      const {
        layerName,
        segmentId,
        seedPosition,
        seedAdditionalCoordinates,
        meshFileName,
        mappingName,
        opacity,
        isVisible,
      } = action;
      const meshInfo: MeshInformation = {
        segmentId: segmentId,
        seedPosition,
        seedAdditionalCoordinates,
        isLoading: false,
        isVisible,
        isPrecomputed: true,
        opacity,
        meshFileName,
        mappingName,
      };
      const additionalCoordinates = state.flycam.additionalCoordinates;
      const additionalCoordKey = getAdditionalCoordinatesAsString(additionalCoordinates);

      const stateWithCurrentAddCoords = maybeAddAdditionalCoordinatesToMeshState(
        state,
        additionalCoordinates,
        layerName,
      );
      const updatedKey = update(stateWithCurrentAddCoords, {
        localSegmentationStateByLayer: {
          [layerName]: {
            meshes: {
              [additionalCoordKey]: {
                [segmentId.toString()]: {
                  $set: meshInfo,
                },
              },
            },
          },
        },
      });
      return updatedKey;
    }

    case "STARTED_LOADING_MESH": {
      const { layerName, segmentId } = action;
      const additionalCoordKey = getAdditionalCoordinatesAsString(
        state.flycam.additionalCoordinates,
      );
      const updatedKey = update(state, {
        localSegmentationStateByLayer: {
          [layerName]: {
            meshes: {
              [additionalCoordKey]: {
                [segmentId.toString()]: {
                  isLoading: {
                    $set: true,
                  },
                },
              },
            },
          },
        },
      });
      return updatedKey;
    }

    case "FINISHED_LOADING_MESH": {
      const { layerName, segmentId } = action;
      const additionalCoordKey = getAdditionalCoordinatesAsString(
        state.flycam.additionalCoordinates,
      );
      const updatedKey = update(state, {
        localSegmentationStateByLayer: {
          [layerName]: {
            meshes: {
              [additionalCoordKey]: {
                [segmentId.toString()]: {
                  isLoading: {
                    $set: false,
                  },
                },
              },
            },
          },
        },
      });
      return updatedKey;
    }

    case "UPDATE_MESH_FILE_LIST": {
      const { layerName, meshFiles } = action;
      return updateKey2(state, "localSegmentationStateByLayer", layerName, {
        availableMeshFiles: meshFiles,
      });
    }

    case "UPDATE_CURRENT_MESH_FILE": {
      const { layerName, meshFileName } = action;
      const availableMeshFiles = state.localSegmentationStateByLayer[layerName].availableMeshFiles;
      if (availableMeshFiles == null) return state;
      const meshFile = availableMeshFiles.find((el) => el.name === meshFileName);
      return updateKey2(state, "localSegmentationStateByLayer", layerName, {
        currentMeshFile: meshFile,
      });
    }

    case "SET_SELECTED_SEGMENTS_OR_GROUP": {
      const { selectedSegments, selectedGroup, layerName } = action;
      return updateKey2(state, "localSegmentationStateByLayer", layerName, {
        selectedIds: { segments: selectedSegments, group: selectedGroup },
      });
    }

    case "SET_COLLABORATION_MODE": {
      return updateKey(state, "annotation", {
        collaborationMode: action.collaborationMode,
      });
    }

    case "SET_ID_RESERVATIONS": {
      // BoundingBox reservations are annotation-wide (bounding boxes are mirrored across all
      // tracings, see updateUserBoundingBoxes above), unlike SegmentGroup/Segment reservations
      // which are handled per segmentation layer in volumetracing_reducer.ts.
      if (action.domain !== "BoundingBox") return state;
      return updateKey(state, "localAnnotationState", {
        idReservationsForBoundingBoxes: action.reservations,
      });
    }

    default:
      return state;
  }
}

export default AnnotationReducer;
